import { Badge } from "@loot-os/ui/components/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";

import { formatDate } from "../lib/format";
import {
  describeOrderActivity,
  describeOrderActivityActor,
  orderActivityInputPairs,
} from "../lib/orderActivity";
import { useTRPC } from "../lib/trpc";
import { ErrorAlert } from "./ErrorAlert";

/**
 * What has happened to an order, newest first: every audited write against it or its lines (staff,
 * the tenant's API, the withdrawal saga), refused attempts included, and the times it reached each
 * status, which is the only record of a move a cron made. `lineNames` names a line-level entry.
 */
export function OrderActivityCard({
  orderId,
  lineNames,
}: {
  orderId: string;
  lineNames: Record<string, string>;
}) {
  const trpc = useTRPC();
  const activity = useQuery(
    trpc.platform.fulfillment.orders.activity.queryOptions({ id: orderId }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
        <CardDescription>
          Audited writes and status times. A status time holds the latest time the order reached it;
          a cron's move has no other entry.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {activity.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : activity.error ? (
          <ErrorAlert message={activity.error.message} />
        ) : !activity.data?.entries.length ? (
          <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
        ) : (
          <ol className="flex flex-col">
            {activity.data.entries.map((entry) => {
              const actor = describeOrderActivityActor(entry);
              const pairs = orderActivityInputPairs(entry, lineNames);
              return (
                <li
                  key={entry.id}
                  className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-4 border-b py-2 text-sm last:border-b-0"
                >
                  <time className="text-muted-foreground tabular-nums" dateTime={String(entry.at)}>
                    {formatDate(entry.at)}
                  </time>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={entry.source === "status" ? "text-muted-foreground" : ""}>
                        {describeOrderActivity(entry)}
                      </span>
                      {entry.lineId ? (
                        <span className="text-muted-foreground">
                          · {lineNames[entry.lineId] ?? entry.lineId}
                        </span>
                      ) : null}
                      {entry.success ? null : (
                        <Badge variant="destructive">
                          refused{entry.errorCode ? `: ${entry.errorCode}` : ""}
                        </Badge>
                      )}
                    </div>
                    {actor || pairs.length ? (
                      <div className="text-xs break-words text-muted-foreground">
                        {[actor, ...pairs.map(([key, value]) => `${key}: ${value}`)]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {activity.data?.truncated ? (
          <p className="pt-2 text-xs text-muted-foreground">
            Older audited writes are not shown: this order has more than this view reads. The full
            history is in the audit log.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
